import type { CSSProperties } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { LineupEntry } from '../../lib/selectors';
import { TaskCheckbox } from '../TaskCheckbox';
import { Avatar } from '../Avatar';
import { DuePill } from '../Pills';
import { CloseIcon, CursorSpark, DragHandle } from '../Icon';
import styles from './LineupDrawer.module.css';

interface SortableLineupRowProps {
  entry: LineupEntry;
  onToggleTask: (taskId: string) => void;
  onSelectTask: (taskId: string) => void;
  onToggleLineup: (taskId: string) => void;
  onCursor: (title: string) => void;
}

/** A single Lineup row, draggable via its handle to reorder within the panel. */
export function SortableLineupRow({
  entry,
  onToggleTask,
  onSelectTask,
  onToggleLineup,
  onCursor,
}: SortableLineupRowProps) {
  const { client, task } = entry;
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style: CSSProperties = {
    // Translate only — never scale — so rows don't warp while dragging.
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 5 : undefined,
    opacity: isDragging ? 0.9 : 1,
    pointerEvents: isDragging ? 'none' : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className={styles.row}>
      <button
        type="button"
        ref={setActivatorNodeRef}
        className={styles.dragHandle}
        title="Drag to reorder"
        aria-label={`Drag to reorder ${task.title}`}
        {...attributes}
        {...listeners}
      >
        <DragHandle size={14} />
      </button>
      <TaskCheckbox
        done={task.done}
        onToggle={() => onToggleTask(task.id)}
        label={`Complete: ${task.title}`}
      />
      <button type="button" className={styles.rowBody} onClick={() => onSelectTask(task.id)}>
        <div className={styles.clientRow}>
          <Avatar client={client} size={16} fontSize="0.5rem" />
          <span className={styles.clientName}>{client.name}</span>
        </div>
        <div className={`${styles.taskTitle} ${task.done ? styles.taskTitleDone : ''}`}>
          {task.title}
        </div>
        <div className={styles.metaRow}>
          {task.dueLabel && <DuePill due={task.due} label={task.dueLabel} />}
          {task.est && <span className={styles.metaText}>{task.est}</span>}
        </div>
      </button>
      <div className={styles.rowActions}>
        <button
          type="button"
          className={styles.cursorBtn}
          onClick={() => onCursor(task.title)}
          title="Hand to Cursor"
          aria-label="Hand to Cursor"
        >
          <CursorSpark size={13} />
        </button>
        <button
          type="button"
          className={styles.removeBtn}
          onClick={() => onToggleLineup(task.id)}
          title="Remove from Lineup"
          aria-label="Remove from Lineup"
        >
          <CloseIcon size={13} />
        </button>
      </div>
    </div>
  );
}
